/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { Button, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { CheckOutlined, CloseCircleFilled } from '@ant-design/icons';
import { useApolloClient, useSubscription } from '@apollo/client';
import { last } from 'lodash';
import classNames from 'classnames';
import { GET_WORKER_MAP } from '@/graphql/cache/playground';
import dayjs from 'dayjs';
import styles from './MiniPlayground.module.less';
import {
  ContextualLearningInfo,
  CreateMiniPlaygroundMutationVariables,
  SentenceExecutionData,
  useCreateMiniPlaygroundMutation,
  useGetContextualLearningInfoByRequestLazyQuery,
  useUpdateLearnedAnswerMutation
} from '../../generated/graphql';
import {
  EditorSourceType,
  ISentence
} from '../../pages/PlaygroundV3/components/Editor/editorInterface';
import Loader, { ILoaderType } from '../Loader';
import ContextualLearningPreview from '../request/LearningMethod/ContextualLearningPreview';
import { RunProviderV2 } from '../../provider/runv2';
import { EditorProvider } from '../../pages/PlaygroundV3/components/Editor/EditorProvider';
import Editor from '../../pages/PlaygroundV3/components/Editor/Editor';
import AppUtil from '../../utils/AppUtil';
import MiniPlaygroundLineResult from './MiniPlaygroundLineResult';
import { ON_WORKER_SENTENCE_UPDATE } from '../../graphql/subscriptions/playground';
import { parseText } from '../../pages/PlaygroundV3/components/RichTextEditor/utils';
import { transformInputToOutput } from '../../pages/PlaygroundV3/components/Editor/editorTree';
import {
  IMiniPlaygroundCallbackData,
  IMiniPlaygroundProps,
  MethodLearningState,
  MiniPlaygroundCallbackType,
  MiniPlaygroundSourceType
} from './interface';
import { QuestionInputType } from '../request/interface';
import { prepareAnswerActionInput } from '../../utils/request';
import EditorHeader from '../../pages/PlaygroundV3/components/EditorHeader/EditorHeader';
import { MINI_PLAYGROUND_CALLBACK_EVENT } from './constants';

const localizedFormat = require('dayjs/plugin/localizedFormat');

dayjs.extend(localizedFormat);

function MiniPlayground(props: IMiniPlaygroundProps) {
  const {
    source,
    miniPlaygroundToken: initialMiniPlaygroundToken,
    miniPlaygroundText,
    document,
    metadata,
    onClose
  } = props;

  const { worker, sentence, token } = document;
  const {
    procedure,
    request,
    methodLearningId,
    methodLearningStateLifecycle,
    methodLearningText
  } = metadata;

  const client = useApolloClient();

  const [miniPlaygroundToken, setMiniPlaygroundToken] = useState<string | null>(
    initialMiniPlaygroundToken || null
  );
  const [editorText, setEditorText] = useState<string | null>(null);
  const [contextualLearnings, setContextualLearnings] = useState<
    ContextualLearningInfo[] | null
  >(null);
  const [selectedStep, setSelectedStep] = useState<ISentence | null>(null);

  const allowCBL = !!request;

  const workerMap = client.readQuery({
    query: GET_WORKER_MAP
  });
  const lineId = useMemo(
    () => workerMap?.getWorkerMap?.idMap[sentence.uiLineId],
    [workerMap, sentence.uiLineId]
  );

  const [createMiniPlayground, createMiniPlaygroundProps] =
    useCreateMiniPlaygroundMutation();

  const [updateLearnedAnswer, updateLearnedAnswerProps] =
    useUpdateLearnedAnswerMutation();

  const [getContextInfo, getContextInfoProps] =
    useGetContextualLearningInfoByRequestLazyQuery({
      variables: {
        requestId: request?.id!
      },
      onCompleted(data) {
        const contextualLearningInfo = data.getContextualLearningInfoByRequest;
        if (contextualLearningInfo) {
          setContextualLearnings(contextualLearningInfo.items || []);
        }
      },
      onError() {
        message.error('Failed to get contextual learning');
      }
    });

  useSubscription(ON_WORKER_SENTENCE_UPDATE, {
    variables: { workerId: worker.id, documentToken: token },
    onData: ({ data }) => {
      const sentenceData: SentenceExecutionData | undefined = (
        data.data?.onSentenceUpdate?.sentences || []
      ).find((sd: SentenceExecutionData) => sd?.lineId === lineId);
      if (sentenceData?.miniPlaygrounds) {
        const miniPlayground = last(sentenceData.miniPlaygrounds);
        setMiniPlaygroundToken(miniPlayground?.documentToken || null);
      }
    }
  });

  const handleNewMiniPlayground = async (options: {
    documentText: string | null;
  }) => {
    try {
      const variables: CreateMiniPlaygroundMutationVariables = {
        input: {
          workerId: worker.id,
          lineId,
          lineToken: sentence.token!,
          document: null
        }
      };

      if (options.documentText) {
        const parsedText = parseText(options.documentText);
        const intermediateLine = parsedText.documentLines.map((line) => {
          const s = line.children[0];
          const output: ISentence = {
            text: s.text,
            uiLineId: s.uiLineId,
            tabCount: s.tabCount,
            language: s.language,
            internalMetadata: s.internalMetadata,
            shouldPause: false
          };
          return output;
        });
        const output = transformInputToOutput(intermediateLine, undefined);
        if (output) {
          variables.input.document = output;
        }
      }

      await createMiniPlayground({
        variables
      });
    } catch (e) {
      message.error('Failed to create a new mini playground');
    }
  };

  const handleCallback = (
    type: MiniPlaygroundCallbackType,
    data?: Partial<IMiniPlaygroundCallbackData>
  ) => {
    const callbackData: IMiniPlaygroundCallbackData = {
      editorText: editorText || '',
      miniPlaygroundToken: miniPlaygroundToken!,
      metadata,
      ...data
    };

    // mini playground can be opened from different sources - all different, so dispatch the callback to all those different sources
    window.document.dispatchEvent(
      new CustomEvent(MINI_PLAYGROUND_CALLBACK_EVENT, {
        detail: { type, data: callbackData }
      })
    );
  };

  const handleMethodLearningUpdate = () => {
    updateLearnedAnswer({
      variables: {
        input: {
          learnedAnswerId: methodLearningId!,
          procedureId: procedure!.id,
          action: prepareAnswerActionInput({
            inputType: QuestionInputType.TECHNIQUE,
            technique: editorText || ''
          })
        }
      },
      onCompleted: () => {
        handleCallback(MiniPlaygroundCallbackType.METHOD_LEARNING_UPDATE);
        message.success('Method learning updated');
        onClose?.();
      },
      onError: () => {
        message.error('Failed to update method learning');
      }
    });
  };

  useEffect(() => {
    if (!initialMiniPlaygroundToken) {
      handleNewMiniPlayground({
        documentText: miniPlaygroundText || null
      });
    }
    // disabling eslint exhaustive deps because we don't want to run this effect when handleNewMiniPlayground method changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMiniPlaygroundToken, miniPlaygroundText]);

  const renderContent = () => {
    if (createMiniPlaygroundProps.loading || !miniPlaygroundToken) {
      return (
        <Loader
          type={ILoaderType.SKELETON}
          skeletonConfig={{
            rows: 10
          }}
        />
      );
    }

    if (contextualLearnings) {
      return (
        <ContextualLearningPreview
          contextualLearning={last(contextualLearnings) || null}
          methodText={editorText || ''}
          onEditClick={() => setContextualLearnings(null)}
        />
      );
    }

    return (
      <RunProviderV2 procedureId={procedure?.id} fetchWorker={false}>
        <EditorProvider
          key={miniPlaygroundToken}
          worker={worker}
          source={EditorSourceType.MINI_PLAYGROUND}
          defaultDocumentToken={miniPlaygroundToken}
        >
          <EditorHeader
            workerId={worker.id}
            documentToken={miniPlaygroundToken}
            miniPlayground={{
              documentLineId: lineId,
              onReset: () => {
                handleNewMiniPlayground({
                  documentText: null
                });
              }
            }}
          />
          <Editor
            workerId={worker.id}
            hide={{
              header: true,
              outputPanel: true,
              chat: true,
              tabNavIfSingle: true
            }}
            hideActiveBlockOnOutsideClick={false}
            getEditorPlainText={(text) => setEditorText(text)}
            onMiniPgAnswerClick={setSelectedStep}
            procedure={procedure}
          />
          {selectedStep && (
            <MiniPlaygroundLineResult
              selectedStep={selectedStep}
              setSelectedStep={setSelectedStep}
            />
          )}
        </EditorProvider>
      </RunProviderV2>
    );
  };

  const renderCBLFooterActions = () => {
    return contextualLearnings ? (
      <Button
        type="primary"
        onClick={() => {
          handleCallback(MiniPlaygroundCallbackType.CBL_USE_METHOD);
          onClose?.();
        }}
        data-cy="mini-playground-cbl-use"
      >
        Use this method
      </Button>
    ) : (
      <Button
        type="primary"
        disabled={!editorText}
        loading={getContextInfoProps.loading}
        onClick={() => {
          handleCallback(MiniPlaygroundCallbackType.CBL_PREVIEW);
          getContextInfo();
        }}
        data-cy="mini-playground-cbl-preview"
      >
        Next
      </Button>
    );
  };

  const renderFooterActions = () => {
    if (
      source === MiniPlaygroundSourceType.TECHNIQUE ||
      source === MiniPlaygroundSourceType.LEARNING_SUGGESTION
    ) {
      // when CBL is allowed
      if (allowCBL) {
        return renderCBLFooterActions();
      }
      return (
        <Button
          type="primary"
          onClick={() => {
            handleCallback(MiniPlaygroundCallbackType.TECHNIQUE_DONE);
            onClose?.();
          }}
          disabled={!editorText}
          icon={<CheckOutlined />}
          data-cy="mini-playground-done"
        >
          Done
        </Button>
      );
    }

    if (source === MiniPlaygroundSourceType.SENTENCE) {
      // when CBL is allowed
      if (allowCBL) {
        return renderCBLFooterActions();
      }
      return (
        <Button
          type="primary"
          onClick={onClose}
          data-cy="mini-playground-close"
        >
          Close
        </Button>
      );
    }

    if (source === MiniPlaygroundSourceType.LEARNING_METHOD) {
      if (methodLearningStateLifecycle![MethodLearningState.FINISHED]) {
        return (
          <Button
            type="primary"
            data-cy="mini-playground-update-learning"
            disabled={editorText?.trim() === methodLearningText?.trim()}
            onClick={handleMethodLearningUpdate}
            loading={updateLearnedAnswerProps.loading}
          >
            Update learning
          </Button>
        );
      }

      if (methodLearningStateLifecycle![MethodLearningState.FAILED]) {
        return renderCBLFooterActions();
      }

      return (
        <Button
          type="primary"
          onClick={onClose}
          data-cy="mini-playground-close"
        >
          Close
        </Button>
      );
    }

    return (
      <Button type="primary" onClick={onClose} data-cy="mini-playground-close">
        Close
      </Button>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.runInfo}>
            {procedure ? AppUtil.removeToFromName(procedure.name) : worker.name}{' '}
            / {dayjs(worker.createdAt).format('llll')}
          </div>
          <div
            className={styles.sentenceInfo}
            data-cy="mini-playground-sentence-text"
          >
            {sentence.text}
          </div>
        </div>
        <CloseCircleFilled
          className={styles.closeIcon}
          onClick={onClose}
          data-cy="mini-playground-close-x"
        />
      </div>

      <div
        className={classNames([
          styles.content,
          { [styles.contentAnswer]: !!selectedStep }
        ])}
      >
        {renderContent()}
      </div>

      <div className={styles.footer}>{renderFooterActions()}</div>
    </div>
  );
}

export default MiniPlayground;
